import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import type { TranslationKeys } from '@order/Localization';
import { useLocalizationContext } from '@order/Localization';

import type { AddSubscriptionPaymentMethodWithInvalidPaymentMethodErrorMutation } from './graphql/AddSubscriptionPaymentMethod.generated';
import {
  useAddSubscriptionPaymentMethodMutation,
  useAddSubscriptionPaymentMethodWithInvalidPaymentMethodErrorMutation,
} from './graphql/AddSubscriptionPaymentMethod.generated';

// ─────────────────────────────────────────────────────────────────────────────

export const useSweetpassPaymentMethodAdd = () => {
  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled', {
    listenForChanges: true,
  });

  // ─── Mutations ───────────────────────────────────────────────────────

  const shouldEnableInvalidPaymentErrorHandling = useFeatureFlag(
    'CELS-1030-short-add-subscription-payment-method-errors-enabled',
  );

  const [
    addPaymentMethodWithInvalidPaymentErrorResponse,
    addPaymentMethodWithInvalidPaymentErrorMutation,
  ] = useAddSubscriptionPaymentMethodWithInvalidPaymentMethodErrorMutation();
  const [
    addPaymentMethodWithoutPaymentErrorResponse,
    addPaymentMethodWithoutPaymentErrorMutation,
  ] = useAddSubscriptionPaymentMethodMutation();

  const addPaymentMethodResponse = shouldEnableInvalidPaymentErrorHandling
    ? addPaymentMethodWithInvalidPaymentErrorResponse
    : addPaymentMethodWithoutPaymentErrorResponse;
  const addPaymentMethodMutation = shouldEnableInvalidPaymentErrorHandling
    ? addPaymentMethodWithInvalidPaymentErrorMutation
    : addPaymentMethodWithoutPaymentErrorMutation;

  const isLoading = addPaymentMethodResponse.fetching;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleAddPaymentMethodError = useCallback(
    (errorResponseType?: AddSubscriptionPaymentMethodErrorResponseType) => {
      const userErrorMessage =
        t(USER_ERROR_MESSAGES[errorResponseType ?? 'default']) ??
        t(USER_ERROR_MESSAGES.default);

      addNoticeBanner({ text: userErrorMessage, palette: 'caution' });
    },
    [addNoticeBanner, t],
  );

  const addPaymentMethod = useCallback(
    async (nonce: string) => {
      const { data } = await addPaymentMethodMutation({ nonce });

      const responseType = data?.addSubscriptionPaymentMethod.__typename;
      const hasSuccessfullyAddedPaymentMethod =
        responseType === 'AddSubscriptionPaymentMethodSuccessResponse';

      if (!hasSuccessfullyAddedPaymentMethod) {
        handleAddPaymentMethodError(responseType);
      }

      return data;
    },
    [addPaymentMethodMutation, handleAddPaymentMethodError],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    addPaymentMethod,
    isSweetpassEnabled,
    isLoading,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const USER_ERROR_MESSAGES: AddSubscriptionPaymentMethodUserErrorMessages = {
  InvalidPaymentMethod: 'sweetpass.payment-method.invalid-payment-method',
  PaymentMethodAlreadyExistsError: 'sweetpass.payment-method.duplicate',
  UnknownUserError: 'sweetpass.payment-method.generic-payment-method-error',
  default: 'sweetpass.payment-method.generic-payment-method-error',
};

// ─── Types ───────────────────────────────────────────────────────────────────

type AddSubscriptionPaymentMethodUserErrorMessages = Record<
  AddSubscriptionPaymentMethodErrorResponseType | 'default',
  TranslationKeys
>;

type AddSubscriptionPaymentMethodErrorResponseType = Exclude<
  AddSubscriptionPaymentMethodResponseType,
  'AddSubscriptionPaymentMethodSuccessResponse'
>;

type AddSubscriptionPaymentMethodResponseType =
  AddSubscriptionPaymentMethodWithInvalidPaymentMethodErrorMutation['addSubscriptionPaymentMethod']['__typename'];
